import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { VideoPlayerHandle } from '@/components/ui/@DesignSystem/VideoPlayer/VideoPlayer';
import type { StretchRange } from '@/components/views/BeraJob/BeraReportV2/utils';

const NO_STRETCH = -1;
const FIRST_STRETCH = 0;
const END_TOLERANCE_IN_SECONDS = 0.15;
const SEEK_LANDING_WINDOW_IN_SECONDS = 1;
const MAX_STALE_TICKS = 4;
const NO_PENDING_SEEK = null;

function findStretchAt(ranges: StretchRange[], time: number): number {
	const index = ranges.findIndex((range) => time >= range.start && time < range.end);

	return index;
}

function findNextStretch(ranges: StretchRange[], time: number): number {
	const index = ranges.findIndex((range) => range.start > time);

	return index;
}

function hasReachedEnd(range: StretchRange, time: number): boolean {
	const reached = time >= range.end - END_TOLERANCE_IN_SECONDS;

	return reached;
}

function resolveLoopTarget(ranges: StretchRange[], time: number): number {
	const next = findNextStretch(ranges, time);

	if (next === NO_STRETCH) {
		return FIRST_STRETCH;
	}

	return next;
}

function hasLandedOn(target: number, time: number): boolean {
	const landed = time >= target - END_TOLERANCE_IN_SECONDS && time <= target + SEEK_LANDING_WINDOW_IN_SECONDS;

	return landed;
}

function toPlayableRanges(ranges: StretchRange[], videoDuration?: number): StretchRange[] {
	if (!videoDuration) {
		return ranges;
	}

	const inside = ranges.filter((range) => range.start < videoDuration);
	const result = inside.map((range) => ({ ...range, end: Math.min(range.end, videoDuration) }));

	return result;
}

interface UseStretchLoopParams {
	playerRef: React.MutableRefObject<VideoPlayerHandle | null>;
	ranges: StretchRange[];
	videoDuration?: number;
}

export function useStretchLoop({ playerRef, ranges, videoDuration }: UseStretchLoopParams) {
	const currentStretch = useRef<number>(NO_STRETCH);
	const pendingSeek = useRef<number | null>(NO_PENDING_SEEK);
	const staleTicks = useRef<number>(0);

	const playableRanges = useMemo(() => toPlayableRanges(ranges, videoDuration), [ranges, videoDuration]);

	useEffect(() => {
		currentStretch.current = NO_STRETCH;
		pendingSeek.current = NO_PENDING_SEEK;
	}, [playableRanges]);

	const markSeek = useCallback((target: number) => {
		pendingSeek.current = target;
		staleTicks.current = 0;
	}, []);

	const isStaleTick = useCallback((playedSeconds: number): boolean => {
		const target = pendingSeek.current;

		if (target === NO_PENDING_SEEK) {
			return false;
		}

		const hasSettled = hasLandedOn(target, playedSeconds) || staleTicks.current >= MAX_STALE_TICKS;

		if (hasSettled) {
			pendingSeek.current = NO_PENDING_SEEK;
			return false;
		}

		staleTicks.current += 1;

		return true;
	}, []);

	const startLoop = useCallback(() => {
		if (!playableRanges.length) {
			return;
		}

		const target = playableRanges[FIRST_STRETCH].start;

		currentStretch.current = FIRST_STRETCH;
		markSeek(target);
		playerRef.current?.expandAndSeekTo(target);
	}, [playerRef, playableRanges, markSeek]);

	const syncCurrentStretch = useCallback(
		(playedSeconds: number) => {
			const found = findStretchAt(playableRanges, playedSeconds);

			if (found === NO_STRETCH) {
				return;
			}

			currentStretch.current = found;
		},
		[playableRanges]
	);

	const loopToNextStretch = useCallback(
		(playedSeconds: number) => {
			const next = resolveLoopTarget(playableRanges, playedSeconds);
			const target = playableRanges[next].start;

			currentStretch.current = next;
			markSeek(target);
			playerRef.current?.seekTo(target);
		},
		[playerRef, playableRanges, markSeek]
	);

	const handleProgress = useCallback(
		(playedSeconds: number) => {
			if (!playableRanges.length) {
				return;
			}

			if (isStaleTick(playedSeconds)) {
				return;
			}

			const playing = currentStretch.current;

			if (playing === NO_STRETCH) {
				syncCurrentStretch(playedSeconds);

				return;
			}

			const reachedEnd = hasReachedEnd(playableRanges[playing], playedSeconds);

			if (!reachedEnd) {
				return;
			}

			loopToNextStretch(playedSeconds);
		},
		[playableRanges, isStaleTick, syncCurrentStretch, loopToNextStretch]
	);

	return { startLoop, handleProgress };
}
